import update from "immutability-helper";
import { partition } from "lodash-es";

import type { BTSelectItem } from "types/apiResponse/apiResponse";
import { ProposalGroupTypes } from "types/enum";

import { asTuple } from "utilities/array/array";
import { reportMessage } from "utilities/errorHelpers";

import { isLineItemRow } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import {
    findCategoryById,
    getFormatGroupsFromFormatItems,
    isProposalSubCategory,
    toRootCategory,
    toSubCategory,
} from "commonComponents/utilities/ProposalBaseLineItemContainer/WorksheetLineItemContainer.utility";

import {
    type FormatItem,
    type FormatItemList,
    isProposalCategory,
    isUnassignedCategory,
    type ProposalCategory,
    type ProposalSubCategory,
    UnassignedCostGroupId,
    type WorksheetLineItem,
} from "entity/estimate/common/estimate.common.types";
import {
    type CostCodeExtraData,
    getFlatRateCostCodeId,
} from "entity/estimate/Estimate/Estimate.api.types";
import {
    getDefaultProposalCategory,
    getUnassignedCategory,
} from "entity/estimate/Proposal/Proposal.types";

/**
 * Ensure only line items are selected
 */
export const unselectGroups = <T extends FormatItem>(formatItems: T[]): T[] => {
    return formatItems.map((formatItem) => {
        if (isProposalCategory(formatItem) && (formatItem.isSelected || formatItem.items)) {
            const category: ProposalCategory = formatItem;

            return update(category, {
                isSelected: { $set: false },
                items: { $set: unselectGroups(formatItem.items) },
            }) as T;
        }
        return formatItem;
    });
};

/**
 * Assumes if something is selected, so are all children
 */
export const pruneSelectedBranchesFromListOfFormatItems = <T extends FormatItem>(
    rootNodes: T[],
) => {
    const prunedSelectedNodes: FormatItem[] = [];
    const prunedUnselectedNodes: T[] = [];

    rootNodes.forEach((node) => {
        if (node.isSelected) {
            prunedSelectedNodes.push(node);
        } else {
            if (isProposalSubCategory(node)) {
                const subCategory: ProposalSubCategory = node;
                const [childSelectedNodes, childUnselectedNodes] =
                    pruneSelectedBranchesFromListOfFormatItems(subCategory.items);

                prunedSelectedNodes.push(...childSelectedNodes);

                if (childSelectedNodes.length) {
                    prunedUnselectedNodes.push(
                        update(subCategory, {
                            items: { $set: childUnselectedNodes },
                        }) as T,
                    );
                    return;
                }
            } else if (isProposalCategory(node)) {
                const category: ProposalCategory = node;
                const [childSelectedNodes, childUnselectedNodes] =
                    pruneSelectedBranchesFromListOfFormatItems(category.items);

                prunedSelectedNodes.push(...childSelectedNodes);

                if (childSelectedNodes.length) {
                    prunedUnselectedNodes.push(
                        update(category, {
                            items: { $set: childUnselectedNodes },
                        }) as T,
                    );
                    return;
                }
            }

            prunedUnselectedNodes.push(node);
        }
    });

    return asTuple(prunedSelectedNodes, prunedUnselectedNodes);
};

export const unselectFormatItemAndChildren = <T extends FormatItem>(rootNode: T): T => {
    if (isProposalCategory(rootNode)) {
        const categoryNode: ProposalCategory = rootNode;
        const newItems = categoryNode.items.map((item) => unselectFormatItemAndChildren(item));

        return update(categoryNode, {
            isSelected: { $set: false },
            items: { $set: newItems },
        }) as T;
    } else {
        const lineItemNode: WorksheetLineItem = rootNode;
        return update(lineItemNode, { isSelected: { $set: false } }) as T;
    }
};

const updateMovingFormatItems = (formatItems: FormatItem[], destinationCategoryId: number) => {
    return formatItems.map((item) => {
        if (isProposalSubCategory(item)) {
            // Is a subcategory
            if (destinationCategoryId === UnassignedCostGroupId) {
                // Make Unassigned Cost Group a root category
                return toRootCategory(item);
            } else if (destinationCategoryId !== item.parentId) {
                // Subcategory moved to a differnt root category
                return update(item, {
                    parentId: { $set: destinationCategoryId },
                    _isDirty: { $set: true },
                });
            }
        } else if (isProposalCategory(item)) {
            // Is a root category
            if (destinationCategoryId !== UnassignedCostGroupId) {
                // The root category is being made into a subcategory
                return toSubCategory(item, destinationCategoryId);
            }
        } else if (isLineItemRow(item)) {
            return update(item, {
                costCategoryFormatId: { $set: destinationCategoryId },
                _isDirty: { $set: true },
            });
        }

        return item;
    });
};

/**
 * Assumes Unassigned Category is at the end of currentMutableHierarchy
 */
const updateUnassignedCategoryAsDestination = (
    currentMutableHierarchy: ProposalCategory[],
    currentUnassignedCategory: ProposalCategory,
    movingRootCategories: ProposalCategory[],
    movingLineItems: WorksheetLineItem[],
    destinationPosition?: number,
) => {
    if (movingRootCategories.length > 0) {
        currentMutableHierarchy.splice(
            destinationPosition ?? currentMutableHierarchy.length - 1,
            0,
            ...movingRootCategories,
        );
    }

    if (movingLineItems.length > 0) {
        return update(currentUnassignedCategory, {
            items: {
                $splice: [
                    [
                        destinationPosition ?? currentUnassignedCategory.items.length,
                        0,
                        ...movingLineItems,
                    ],
                ],
            },
        });
    }

    return currentUnassignedCategory;
};

/**
 * Insert items in default positions
 * Category contents consists of all line items followed by all subgroups
 *
 * When inserting new items, put them at the bottom of their section
 */
export const updateCategoryUsingDefaultPositioning = (
    currentDestination: ProposalCategory,
    movingSubCategories: ProposalSubCategory[],
    movingLineItems: WorksheetLineItem[],
) => {
    let indexAfterLineItems = currentDestination.items.findIndex(isProposalCategory);
    if (indexAfterLineItems === -1) {
        indexAfterLineItems = currentDestination.items.length;
    }

    return update(currentDestination, {
        items: {
            $splice: [
                [currentDestination.items.length, 0, ...movingSubCategories],
                [indexAfterLineItems, 0, ...movingLineItems],
            ],
        },
    });
};

const UpdateHierarchyWithUpdatedDestination = (
    currentMutableHierarchy: ProposalCategory[],
    updatedDestination: ProposalCategory,
) => {
    if (updatedDestination.id === UnassignedCostGroupId) {
        currentMutableHierarchy[currentMutableHierarchy.length - 1] = updatedDestination;
    } else if (isProposalSubCategory(updatedDestination)) {
        const destinationAsSubCategory: ProposalSubCategory = updatedDestination;

        const parentOfDestinationIndex = currentMutableHierarchy.findIndex(
            (item) => item.id === destinationAsSubCategory.parentId,
        );
        const parentOfDestination = currentMutableHierarchy[parentOfDestinationIndex];
        const newParentItems = parentOfDestination.items.map((item) =>
            item.id === destinationAsSubCategory.id ? destinationAsSubCategory : item,
        );

        currentMutableHierarchy[parentOfDestinationIndex] = update(parentOfDestination, {
            items: {
                $set: newParentItems,
            },
        });
    } else {
        const destinationIndex = currentMutableHierarchy.findIndex(
            (item) => item.id === updatedDestination?.id,
        );

        currentMutableHierarchy[destinationIndex] = updatedDestination;
    }
    updatedDestination._isDirty = true;

    updateDisplayOrderInFormatItems(currentMutableHierarchy);
    return currentMutableHierarchy;
};

const updateDisplayOrderInFormatItems = (formatItems: FormatItem[]) => {
    formatItems.forEach((fi, index) => {
        const newDisplayOrder = index + 1; // display order is 1-indexed
        if (fi.displayOrder !== newDisplayOrder && fi.id !== UnassignedCostGroupId) {
            fi._isDirty = true;
            fi.displayOrder = newDisplayOrder;
        }
        if (isProposalCategory(fi) || isProposalSubCategory(fi)) {
            updateDisplayOrderInFormatItems(fi.items);
        }
    });
};

/**
 * Method for handling the insertion and adjustment of existing format items that
 * are in the process of being moved. Meant to be used for all move actions including
 * from checked actions and when using drag and drop. Assumes that the moving items
 * have already been removed from the list of unmoving categories.
 *
 * @param destinationPosition the index within the destination to insert
 * the items at. Omitting it will move the items to a default position instead.
 */
export const insertFormatItemsIntoList = (
    builderId: number,
    unmovingFormatItems: ProposalCategory[],
    movingFormatItems: FormatItem[],
    destinationCategoryId: number,
    destinationPosition?: number,
) => {
    const allUnmovingCategories = getFormatGroupsFromFormatItems(unmovingFormatItems, true);

    let destination = allUnmovingCategories.find(
        (category) => category.id === destinationCategoryId,
    );

    if (!destination) {
        // The user may select the unassigned category even though it may not exist yet on the format
        // Create it if so
        if (destinationCategoryId === UnassignedCostGroupId) {
            destination = getUnassignedCategory(builderId);
            unmovingFormatItems.push(destination);
        } else {
            // This shouldn't happen
            return undefined;
        }
    }

    const updatedMovingFormatItems = updateMovingFormatItems(
        movingFormatItems,
        destinationCategoryId,
    );

    const newFormatItems = [...unmovingFormatItems];
    const [movingRootAndSubCategories, movingLineItems] = partition(
        updatedMovingFormatItems,
        isProposalCategory,
    );
    const [movingSubCategories, movingRootCategories] = partition(
        movingRootAndSubCategories,
        isProposalSubCategory,
    );

    // add the moving items to the destination
    if (destination.id === UnassignedCostGroupId) {
        destination = updateUnassignedCategoryAsDestination(
            newFormatItems,
            destination,
            movingRootCategories,
            movingLineItems,
            destinationPosition,
        );
    } else if (destinationPosition !== undefined) {
        // If moving a subgroup, make sure to insert it after the group's line items.
        if (movingSubCategories.length === 1 && movingLineItems.length === 0) {
            const firstSubgroupInGroupIndex = destination.items.findIndex(isProposalSubCategory);
            if (destinationPosition < firstSubgroupInGroupIndex) {
                destinationPosition = firstSubgroupInGroupIndex;
            }
        }
        destination = update(destination, {
            items: {
                $splice: [[destinationPosition, 0, ...movingSubCategories, ...movingLineItems]],
            },
        });
    } else {
        destination = updateCategoryUsingDefaultPositioning(
            destination,
            movingSubCategories,
            movingLineItems,
        );
    }

    return UpdateHierarchyWithUpdatedDestination(newFormatItems, destination);
};

/**
 * A group's cost category. Only top-level category groups carry one, so a subgroup resolves
 * through its parent. `null` when neither has one (e.g. Unassigned) or the group isn't found.
 */
export const getGroupCostCategoryId = (
    groupId: number,
    formatData: FormatItemList,
): number | null => {
    let group = findCategoryById(groupId, formatData);
    while (group) {
        if (group.costCategoryId != null) {
            return group.costCategoryId;
        }
        group = group.parentId == null ? undefined : findCategoryById(group.parentId, formatData);
    }
    return null;
};

/**
 * The cost category an item edit limits its cost codes to: the group's category under cost code grouping
 * in the Proposal Worksheet view, `null` (no limit) anywhere else. The Cost Code view regroups by cost
 * code on save, so it never limits the picker.
 */
export const getItemEditCostCategoryId = (
    groupId: number,
    formatData: FormatItemList,
    viewType: ProposalGroupTypes,
    groupingType: ProposalGroupTypes,
): number | null =>
    viewType === ProposalGroupTypes.ProposalWorksheet &&
    groupingType === ProposalGroupTypes.CostCategory
        ? getGroupCostCategoryId(groupId, formatData)
        : null;

/**
 * The cost codes in `costCategoryId` (all of them when `null`), plus `currentCostCodeId` so an
 * item already holding an out-of-category code still shows it. Under cost code grouping, a code
 * from another category would move the item out of its group on save.
 */
export const getCostCodesForCostCategory = <T extends { costCategoryId?: number | null }>(
    costCodes: BTSelectItem<T>[],
    costCategoryId: number | null,
    currentCostCodeId?: number | null,
): BTSelectItem<T>[] =>
    costCategoryId == null
        ? costCodes
        : costCodes.filter(
              (costCode) =>
                  costCode.extraData?.costCategoryId === costCategoryId ||
                  (currentCostCodeId != null && Number(costCode.value) === currentCostCodeId),
          );

/**
 * The cost code an item takes when moved into `newCostCategoryId`: the default when its current code
 * is in another category, else `null` to keep it. A `null` category constrains nothing.
 */
export const getCostCodeForParentChange = <T extends { costCategoryId?: number | null }>(
    currentCostCodeId: number | null | undefined,
    costCodes: BTSelectItem<T>[],
    newCostCategoryId: number | null,
    getDefaultCostCodeId: () => number | null,
): number | null => {
    if (newCostCategoryId == null) {
        return null;
    }
    const currentCostCategoryId =
        costCodes.find((costCode) => Number(costCode.value) === currentCostCodeId)?.extraData
            ?.costCategoryId ?? null;
    return currentCostCategoryId === newCostCategoryId ? null : getDefaultCostCodeId();
};

/**
 * Default cost code for a line item added to `groupId`: the category's first real code, skipping
 * the one that just stands in for the category (matched by title) unless it's the only one, else
 * flat rate. Outside cost code grouping groups carry no category, so it's always flat rate.
 */
export const getDefaultCostCodeIdForGroup = (
    groupId: number | null | undefined,
    formatData: FormatItemList,
    costCodes: BTSelectItem<CostCodeExtraData>[],
    costCategories: readonly BTSelectItem[],
): number | null => {
    const costCategoryId = groupId == null ? null : getGroupCostCategoryId(groupId, formatData);

    if (costCategoryId != null) {
        const categoryCostCodes = getCostCodesForCostCategory(costCodes, costCategoryId);
        const categoryTitle = costCategories.find(
            (category) => Number(category.value) === costCategoryId,
        )?.title;
        const defaultCostCode =
            categoryCostCodes.find((costCode) => costCode.title !== categoryTitle) ??
            categoryCostCodes[0];

        if (defaultCostCode?.value != null) {
            return Number(defaultCostCode.value);
        }
    }

    return getFlatRateCostCodeId(costCodes);
};

/**
 * The cost category and cost code title a saved line item carries, derived from its cost code. A new
 * inline row has neither, and Cost Code view regroups on `costCategoryId` (and sorts on the title),
 * so a row saved without them is dropped from its group until the worksheet is refetched.
 */
export const getCostCodeCategoryValues = (
    costCodeId: number | null | undefined,
    costCodes: BTSelectItem<CostCodeExtraData>[],
): { costCategoryId: number | null; costCodeTitle: string | undefined } => {
    const costCode = costCodes.find((c) => c.value === costCodeId);
    return {
        costCategoryId: costCode?.extraData?.costCategoryId ?? null,
        costCodeTitle: costCode?.title ?? undefined,
    };
};

/**
 * The format group to send as a new inline row's parent. Cost Code view's group ids are cost
 * category ids (`mapGridDataToFormatItems`), which aren't valid parents. Under cost code grouping
 * they map to the category's group, else Unassigned so the server places the item by cost code;
 * under custom grouping they're always Unassigned. Other views' ids pass through.
 */
export const getParentFormatGroupIdForViewGroup = (
    viewGroupId: number | null,
    formatData: ProposalCategory[],
    isCostCodeView: boolean,
    estimateFormatGroupType: ProposalGroupTypes,
): number | null => {
    if (viewGroupId == null || !isCostCodeView) {
        return viewGroupId;
    }
    if (estimateFormatGroupType === ProposalGroupTypes.CostCategory) {
        return (
            formatData.find((category) => category.costCategoryId === viewGroupId)?.id ??
            UnassignedCostGroupId
        );
    }
    return UnassignedCostGroupId;
};

/**
 * The group id a new line item will land in, predicting how the server derives placement. -1 is
 * the Unassigned bucket. `undefined` means the destination can't be known yet and the caller must
 * refetch.
 *
 * Pre-save only. Once the add returns, `AutosaveLineItemResponse.parentFormatItemId` is authoritative.
 *
 * The `estimateGroupType` (the *view*) is deliberately not an input: `formatData` is built from the
 * grouping alone, so the view can't change where a row belongs.
 */
export const resolveWorksheetParentGroupId = (
    formatData: ProposalCategory[],
    requestedParentId: number | null | undefined,
    costCodeId: number | null | undefined,
    estimateFormatGroupType: ProposalGroupTypes,
    costCodes: readonly BTSelectItem<CostCodeExtraData>[],
): number | undefined => {
    if (estimateFormatGroupType === ProposalGroupTypes.CostCategory) {
        // The server honors a requested subgroup; anything else is placed in the category group
        // for the cost code (created if missing).
        const requestedGroup =
            requestedParentId == null || requestedParentId === UnassignedCostGroupId
                ? undefined
                : findCategoryById(requestedParentId, formatData);
        if (requestedGroup && isProposalSubCategory(requestedGroup)) {
            return requestedGroup.id;
        }

        const costCategoryId =
            costCodeId == null
                ? null
                : (costCodes.find((costCode) => Number(costCode.value) === costCodeId)?.extraData
                      ?.costCategoryId ?? null);

        if (costCategoryId != null) {
            const categoryGroup = formatData.find(
                (category) => category.costCategoryId === costCategoryId,
            );
            // No group for the category yet means the server is about to mint one, and its id can't
            // be known until the save responds. Expected for the first item into a category, so it
            // isn't worth a Sentry signal.
            return categoryGroup?.id;
        }
        // An unknown cost code mirrors the server falling through to ParentId (with -1 normalized to
        // null), which is the Unassigned tail below.
    } else if (requestedParentId != null && requestedParentId !== UnassignedCostGroupId) {
        // Assembly/custom grouping: the server honors the requested parent, sub-groups included.
        if (findCategoryById(requestedParentId, formatData)) {
            return requestedParentId;
        }
        reportMessage(
            `resolveWorksheetParentGroupId: requested group ${requestedParentId} is not in the worksheet — stale format data`,
        );
        return undefined;
    }

    // Unassigned is emitted for every grouping, so -1 is always a valid destination — whether or not
    // the bucket is currently in the tree (the client strips it when empty).
    return UnassignedCostGroupId;
};

/**
 * Adds a group the server minted for `costCategoryId` but that isn't in the local tree yet, so a
 * newly saved line item has somewhere to live without a full refetch. Inserted before Unassigned,
 * which always sorts last. Returns the list unchanged when the group already exists.
 *
 * The synthesized group carries default `optionMode`/`attachedFiles` and a guessed display order
 * until the next load, the same tradeoff {@link getUnassignedCategory} has always made.
 */
export const addCostCategoryGroup = (
    formatItems: ProposalCategory[],
    newGroup: { id: number; builderId: number; costCategoryId: number; title: string },
): ProposalCategory[] => {
    if (formatItems.some((category) => category.id === newGroup.id)) {
        return formatItems;
    }

    // jobId -1 mirrors getUnassignedCategory: a synthesized group isn't saved and can't take
    // attachments, so the id it would scope them to doesn't matter.
    const category = getDefaultProposalCategory(-1, newGroup.builderId, newGroup.id);
    category.costCategoryId = newGroup.costCategoryId;
    category.title = newGroup.title;

    const unassignedIndex = formatItems.findIndex(isUnassignedCategory);
    const hasUnassigned = unassignedIndex >= 0;
    const updated = [...formatItems];
    updated.splice(hasUnassigned ? unassignedIndex : updated.length, 0, category);
    return updated;
};

/**
 * Inserts an unsaved format item (negative id) at the top of its destination, so the worksheet can
 * render it as a real row instead of tracking a separate "pending" row. A top-level group goes to
 * the front of the list; anything else to the front of `parentGroupId`'s children.
 *
 * Materializes the Unassigned bucket when `parentGroupId` is -1 and it isn't in the tree, matching
 * what {@link insertFormatItemsIntoList} already does for move destinations.
 *
 * Returns `undefined` when the item couldn't be placed — a missing destination or an item that can't
 * live there — so the caller can refetch instead of silently dropping the row.
 */
export const insertNewFormatItemAtTop = (
    formatItems: ProposalCategory[],
    newItem: FormatItem,
    parentGroupId: number | null,
    builderId: number,
): ProposalCategory[] | undefined => {
    const isTopLevelGroup = isProposalCategory(newItem) && !isProposalSubCategory(newItem);

    if (parentGroupId == null) {
        // Only a top-level group can live at the root of the worksheet.
        return isTopLevelGroup ? [newItem, ...formatItems] : undefined;
    }
    // A group's children are line items and sub-groups only, so a top-level group can't nest.
    if (isTopLevelGroup) {
        return undefined;
    }

    let inserted = false;

    const insertIntoCategory = <T extends ProposalCategory>(category: T): T => {
        // Sub-groups sort after a group's line items, so each kind goes to the top of its own section
        // rather than blindly at index 0 — a sub-group added to a group with no other sub-groups
        // therefore lands at the end, after the line items.
        const firstSubGroupIndex = category.items.findIndex(isProposalCategory);
        const insertAt = !isProposalSubCategory(newItem)
            ? 0
            : firstSubGroupIndex === -1
              ? category.items.length
              : firstSubGroupIndex;
        const items = [...category.items];
        items.splice(insertAt, 0, newItem);
        inserted = true;
        return { ...category, items };
    };

    // The client strips the Unassigned bucket when it's empty, so the destination may need creating.
    const destinations =
        parentGroupId === UnassignedCostGroupId && !formatItems.some(isUnassignedCategory)
            ? [...formatItems, getUnassignedCategory(builderId)]
            : formatItems;

    const updated = destinations.map((category) => {
        if (category.id === parentGroupId) {
            return insertIntoCategory(category);
        }
        if (!getSubCategories(category).some((sub) => sub.id === parentGroupId)) {
            return category;
        }
        return {
            ...category,
            items: category.items.map((item) =>
                isProposalSubCategory(item) && item.id === parentGroupId
                    ? insertIntoCategory(item)
                    : item,
            ),
        };
    });

    return inserted ? updated : undefined;
};

/**
 * Removes an unsaved format item by id from anywhere in the hierarchy — the undo for
 * {@link insertNewFormatItemAtTop} when the user abandons a new row.
 */
export const removeFormatItemById = (
    formatItems: ProposalCategory[],
    itemId: number,
): ProposalCategory[] =>
    formatItems
        .filter((category) => category.id !== itemId)
        .map((category) => ({
            ...category,
            items: category.items
                .filter((item) => item.id !== itemId)
                .map((item) =>
                    isProposalSubCategory(item)
                        ? {
                              ...item,
                              items: item.items.filter((child) => child.id !== itemId),
                          }
                        : item,
                ),
        }));

/**
 * The saved line items a new row is ordered against. `excludeId` drops the unsaved row whose order
 * is being computed. A destination that isn't in the tree — an absent Unassigned bucket, or a group
 * the server has yet to mint — genuinely has no siblings, so an empty list is the right answer.
 */
export const getLineItemSiblings = (
    formatItems: ProposalCategory[],
    parentGroupId: number,
    excludeId: number,
): WorksheetLineItem[] => {
    const parentGroup = findCategoryById(parentGroupId, formatItems);
    return (parentGroup?.items.filter(isLineItemRow) ?? []).filter((li) => li.id !== excludeId);
};

/**
 * Display order placing a new row above its siblings: the current minimum, which the backend
 * vacates by shifting the existing rows down. Using the min rather than min − 1 keeps the value
 * inside the existing range, so it can't go negative on a 0-based bucket.
 */
export const getTopDisplayOrder = (
    siblings: readonly { displayOrder?: number | null }[],
): number => {
    const orders = siblings.map((s) => s.displayOrder).filter((o): o is number => o != null);
    return orders.length > 0 ? Math.min(...orders) : 0;
};

/**
 * Return sub categories of the passed in proposal category
 */
export function getSubCategories(category: ProposalCategory): ProposalSubCategory[] {
    return category.items.filter(isProposalSubCategory);
}

/**
 * Return whether or not the item (represented by id) is a child of a sub category
 */
export function isChildOfSubCategory(formatItems: FormatItem[], itemId: number): boolean {
    for (const formatItem of formatItems) {
        if (isProposalCategory(formatItem)) {
            if (formatItem.id === itemId) {
                return false;
            }

            for (const formatItemChild of formatItem.items) {
                if (formatItemChild.id === itemId) {
                    return false;
                }

                if (isProposalSubCategory(formatItemChild)) {
                    for (const subCategoryChild of formatItemChild.items) {
                        if (subCategoryChild.id === itemId) {
                            return true;
                        }
                    }
                }
            }
        }
    }
    return false;
}
